// React
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Third-party styling
import styled from "styled-components";

// External libraries
import _ from "underscore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Link,
  ListItem,
  IconButton,
  Typography,
  Stack,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Aliased modules
import { AuthenticationContext, logUserIn } from "@cloudmlm/cloudmlm-auth";

// Project-specific imports
import dataConstant from "../constants/dataConstants";
import urlConstant from "../constants/urlConstants";
import { httpGet } from "../../src/services/commonService";
import { startApiLoader, endApiLoader } from "../services/apiLoaderService";
import store from "../redux/store";
import { login } from "../redux/slice/userSlice";

// React Router
import { useNavigate } from "react-router-dom";

const LoginContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
`;

const LoginWrapper = styled.div`
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  padding: 25px;
  background: var(--black);
  border: 1px solid var(--white);
  border-radius: 20px;
`;

const LoginInput = styled.input`
  margin: 10px 0 !important;
  padding: 15px;
  font-size: 16px;
  width: 100%;
  background-color: var(--inputs);
  color: var(--black);
  border-radius: 12px;
  min-height: 40px;
  outline: none;
  border: 0;
`;

const LoginPage = () => {
  const { t } = useTranslation();
  const authenticationState = useContext(AuthenticationContext);
  const history = useNavigate();
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const [getURL, setURL] = useState("");
  const [isAventi, setIsAventi] = useState(false);
  const [state, setState] = useState({
    loginCredentialsRequired: false,
    loginCredentialsInCorrect: false,
    isSignupOptionPopupOpen: false,
    countryCode: "",
    sigupOptionList: [],
  });

  useEffect(() => {
    if (authenticationState.isLoggedIn) {
      const previousPath = localStorage.getItem("previousPath");
      if (previousPath !== null && previousPath === "aventitravel") {
        localStorage.removeItem("previousPath");
        localStorage.setItem("aventitravelTrip", true);
        //history(`/${previousPath}`);
      } else {
        localStorage.removeItem("previousPath");
        history("/");
      }
    }
  }, [authenticationState.isLoggedIn, history]);

  useEffect(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const repURL = window.location.pathname.split("/");
    const URLrepSiteName = repURL.indexOf("login");
    if (URLrepSiteName !== -1 && URLrepSiteName < repURL.length - 1) {
      const name = repURL[URLrepSiteName + 1];
      if (name !== "") {
        setURL(name);
      }
    }
    setIsAventi(window.location.pathname.toLowerCase().includes("aventitravel"));
  }, []);

  const handleLogin = (event) => {
    if (
      _.isEmpty(emailRef.current.value) ||
      _.isEmpty(passwordRef.current.value)
    ) {
      setState((prevState) => ({
        ...prevState,
        loginCredentialsRequired: true,
        loginCredentialsInCorrect: false,
      }));
      return;
    }

    event.preventDefault();
    startApiLoader();
    logUserIn({
      username: emailRef.current.value,
      password: passwordRef.current.value,
      tokenUrl: process.env.REACT_APP_ROPC_TOKEN_URL,
      grantType: "password",
      scope: `${process.env.REACT_APP_AZURE_AD_B2C_SCOPE} openid offline_access`,
      clientId: process.env.REACT_APP_AZURE_AD_B2C_CLIENTID,
      responseType: "token",
    })
      .then((result) => {
        store.dispatch(
          login({
            name: emailRef.current.value,
            authenticated: true,
          })
        );
        authenticationState.logUserIn(result);
        endApiLoader();
      })
      .catch((e) => {
        setState((prevState) => ({
          ...prevState,
          loginCredentialsInCorrect: true,
          loginCredentialsRequired: false,
        }));
        endApiLoader();
      });
  };

  const handleSignupOptionPopupOpen = () => {
    setState((prevState) => ({
      ...prevState,
      isSignupOptionPopupOpen: true,
    }));
  };

  const handleSignupOptionPopupClose = () => {
    setState((prevState) => ({
      ...prevState,
      isSignupOptionPopupOpen: false,
    }));
  };

  useEffect(() => {
    if (state.countryCode === "") {
      httpGet(`${urlConstant.Login.GetSignupOptionsCountryWise}`, false, true)
        .then((response) => {
          if (response.Status === dataConstant.APIResponseStatus.Success) {
            setState((prevState) => ({
              ...prevState,
              countryCode: response.Data?.CountryCode,
              sigupOptionList: response.Data?.SigupOptions,
            }));
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  },[]);

  return (
    <LoginContainer>
      <LoginWrapper>
        <form className="login-form">
          <Stack>
            <img
              src={isAventi ? `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/AventiLogo-White.png` : `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/logo-bo-login.png` }
              className="logo"
              alt="logo"
            />
          </Stack>
          {state.loginCredentialsInCorrect && (
            <Alert
              sx={(theme) => ({
                background: theme.palette.common.errorbg,
                color: theme.palette.common.errortext,
                marginBottom: "5px",
              })}
              variant="filled"
              severity="info"
            >
              {t("LoginPage.LoginCredentialsInCorrect")}
            </Alert>
          )}
          {state.loginCredentialsRequired && (
            <Alert
              sx={(theme) => ({
                background: theme.palette.common.errorbg,
                color: theme.palette.common.errortext,
                marginBottom: "5px",
              })}
              variant="filled"
              severity="info"
            >
              {t("LoginPage.LoginCredentialsRequired")}
            </Alert>
          )}
          <Typography variant="body2" mb={0.1} color="var(--secondary-text)">
            {t("LoginPage.EmailAddress")}
          </Typography>
          <LoginInput
            className="form-control"
            type="email"
            placeholder={t("LoginPage.EmailAddressPlaceholder")}
            ref={emailRef}
            required={true}
          />
          <Typography
            variant="body2"
            mt={1}
            mb={0.1}
            color="var(--secondary-text)"
          >
            {t("LoginPage.Password")}
          </Typography>
          <LoginInput
            className="form-control"
            type="password"
            placeholder={t("LoginPage.PasswordPlaceholder")}
            ref={passwordRef}
            required={true}
          />
          <Stack textAlign={"right"} width={"100%"}>
            <Typography variant="body2">
              <Link
                href={process.env.REACT_APP_PASSWORD_RESET_URL}
                underline="hover"
              >
                {t("LoginPage.ForgotPassword?")}
              </Link>
            </Typography>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={handleLogin}
            sx={{
              px: 5,
              width: "100%",
              fontSize: "16px",
              fontWeight: 800,
              mt: 2,
            }}
          >
            {t("LoginPage.Login")}
          </Button>
          <Typography variant="body2" mt={1} color="secondary">
            {t("LoginPage.NoAccount?")}{" "}
            <Link
              onClick={handleSignupOptionPopupOpen}
              style={{
                color: "common.white",
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              {t("LoginPage.CreateAccountHere")}
            </Link>
          </Typography>
        </form>
      </LoginWrapper>

      <Dialog
        className="login-popup"
        sx={{ borderRadius: "20px", background: "transparent" }}
        open={state.isSignupOptionPopupOpen}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle>{t("Common.SignUp")}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleSignupOptionPopupClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={{
            background: "var(--background-card)!important",
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
          dividers
        >
          {state.sigupOptionList?.map((item, index) => {
            return (
              <ListItem key={index} sx={{ p: 0, gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: "capitalize", width: "100%" }}
                  onClick={() => history(`${isAventi ? item.Item6 : item.Item3}${isAventi ? "" : getURL}`)}
                >
                  {isAventi ? item.Item2 : item.Item5} 
                </Button>
              </ListItem>
            );
          })}
        </DialogContent>
      </Dialog>
    </LoginContainer>
  );
};

export default LoginPage;
